import {jsx} from 'panel';
import type {VNode} from 'snabbdom';
import isFunction from 'lodash/isFunction';

import {SvgIconEl} from 'common/components/svg-icon';
import {SpinnerEl} from 'common/components/spinner';
import {TooltipEl} from 'common/components/tooltip';
import {Placement, PositioningStrategy} from 'common/components/tooltip/types';
import {unwrapSection} from 'common/components/items-menu/utils';

import {
  ItemAndEventHandler,
  ItemHandler,
  SectionItemContextPanelTransform,
  WrappedSection,
  WrappedSectionItem,
} from '../types';

const TooltipWrapper = (
  {item, isExperimental}: {item: WrappedSectionItem; isExperimental: boolean},
  sectionItemElChild,
) => {
  const sectionItem = item.wrapped;
  // for items with nested sections, avoid displaying the tooltip on hover
  if (!sectionItem.tooltip?.text || (sectionItem.subSections && !sectionItem.isDisabled)) {
    return sectionItemElChild;
  }

  return (
    <>
      <TooltipEl
        attrs={{
          placement: Placement.Right,
          ...(isExperimental && {'position-strategy': PositioningStrategy.Absolute}),
          ...(sectionItem.tooltip ?? {}),
        }}
      />
      <section sel='.list-option-wrapper'>{sectionItemElChild}</section>
    </>
  );
};

export const SectionRowEl = (
  {
    allowPointerEventsAlways,
    item,
    isFocused,
    isMultiple,
    isExperimental,
    contextPanelTransform,
    onClick,
    onMouseEnter,
    onMouseLeave,
  }: {
    allowPointerEventsAlways: boolean;
    item: WrappedSectionItem;
    isFocused: boolean;
    isMultiple: boolean;
    contextPanelTransform: SectionItemContextPanelTransform;
    onClick: ItemAndEventHandler;
    onMouseEnter: ItemHandler;
    onMouseLeave: ItemHandler;
    isExperimental: boolean;
  },
  sectionItemElChild?: VNode,
) => {
  const sectionItem = item.wrapped;
  const hasSubSections = sectionItem.subSections && sectionItem.subSections.length;
  const isClickEnabled = !(sectionItem.isDisabled || sectionItem.isReadOnly);
  const selectionType = isMultiple ? `multiple` : `select`;
  return (
    <div class={{highlight: sectionItem.highlightItem}}>
      <li
        sel='.list-option'
        class={{
          [`for-${selectionType}-type`]: true,
          disabled: sectionItem.isDisabled,
          expander: sectionItem.isExpander,
          focused: isFocused,
          'read-only': sectionItem.isReadOnly,
          selectable: !(sectionItem.isDisabled || sectionItem.isReadOnly),
          selected: sectionItem.isSelected,
          'has-tooltip': sectionItem.tooltip && sectionItem.tooltip.text,
          'allow-pointer-events': allowPointerEventsAlways || contextPanelTransform,
          'has-sub-sections': hasSubSections,
          [`label-theme-${sectionItem.labelTheme}`]: sectionItem.labelTheme,
        }}
        attrs={{
          elref: sectionItem.elref,
        }}
        on={{
          click: isClickEnabled && ((ev) => onClick(item, ev)),
          mouseenter: () => onMouseEnter(item),
          mouseleave: () => onMouseLeave(item),
        }}
      >
        <TooltipWrapper item={item} isExperimental={isExperimental}>
          {sectionItemElChild}
        </TooltipWrapper>
      </li>
    </div>
  );
};

export const SectionEl = (
  {
    section,
    hideSectionTitle = false,
    isExperimental = false,
  }: {
    section: WrappedSection;
    hideSectionTitle?: boolean;
    isExperimental?: boolean;
  },
  sectionItemElChildren?: VNode[],
) => {
  const {sectionLabelIconRenderer, label, tooltip, icon, isLoading} = section;
  const sectionHasHeader = Boolean(!hideSectionTitle && label);
  return (
    <div sel='.section-container' class={{'has-header': sectionHasHeader}}>
      {sectionHasHeader && (
        <div sel='.section-header'>
          <span sel='.label'>{label}</span>
          {isFunction(sectionLabelIconRenderer) && sectionLabelIconRenderer(unwrapSection(section))}
          {icon ? (
            <div sel='.section-icon.item-icon' attrs={{icon}}>
              {tooltip && (
                <TooltipEl
                  attrs={{
                    placement: Placement.RightCenter,
                    ...(isExperimental && {'position-strategy': PositioningStrategy.Absolute}),
                    ...(tooltip ?? {}),
                  }}
                />
              )}
              <SvgIconEl attrs={{icon, empty: !icon}} />
            </div>
          ) : null}
        </div>
      )}
      {isLoading ? (
        <div sel='.loading-items'>
          <SpinnerEl />
        </div>
      ) : (
        <ul sel='.screen-options-list'>{sectionItemElChildren}</ul>
      )}
    </div>
  );
};
